import type { LDFlagSet } from "launchdarkly-js-client-sdk";
import { call } from "redux-saga/effects";

import type { BillingPlan } from "@superblocksteam/shared";

import { isCloudPrem } from "core/utils/deployment";
import { SessionStorageKey } from "legacy/utils/sessionStorage";
import { createSaga } from "store/utils/saga";
import { ROOT } from "store/utils/types";

import flagClient from "../client";
import { Flag } from "../models/Flags";
import type { Flags } from "../models/Flags";
import slice from "../slice";

interface GetFeatureFlagsPayload {
  organizationId: string;
  organizationBillingPlan: BillingPlan;
  created: string;
  // Absent when the server has no flag bootstrap to send.
  flags: LDFlagSet | undefined;
  organizationName?: string;
}

export function* getFeatureFlagsInternal({
  organizationId,
  organizationBillingPlan,
  created,
  flags,
  organizationName,
}: GetFeatureFlagsPayload) {
  const url = new URL(window.location.href);

  const cloudPrem = isCloudPrem();
  const rawFlagOverrides = sessionStorage.getItem(
    SessionStorageKey.FEATURE_OVERRIDES,
  );
  let flagOverrides: Record<string, unknown> = {};
  try {
    flagOverrides = rawFlagOverrides ? JSON.parse(rawFlagOverrides) : {};
    url.searchParams.forEach((flagName, op) => {
      // Without a server bootstrap the known flag names are unknown. Only cloud-prem, which has no
      // LaunchDarkly client, accepts any override then.
      if (flags ? typeof flags[flagName] !== "undefined" : cloudPrem) {
        if (op === "sb_ff_on") {
          flagOverrides[flagName] = true;
        } else if (op === "sb_ff_off") {
          flagOverrides[flagName] = false;
        }
      }
    });

    sessionStorage.setItem(
      SessionStorageKey.FEATURE_OVERRIDES,
      JSON.stringify(flagOverrides),
    );
  } catch (e) {
    // Noop
  }

  // Sales and announcement modal config flags shouldn't be overwritten.
  if (Flag.SALES_SETTINGS_JSON in flagOverrides) {
    delete flagOverrides[Flag.SALES_SETTINGS_JSON];
  }
  if (Flag.ANNOUNCEMENT_MODAL_CONFIG in flagOverrides) {
    delete flagOverrides[Flag.ANNOUNCEMENT_MODAL_CONFIG];
  }

  const response: Flags = yield call(
    flagClient.getKeysForOrg,
    organizationId,
    organizationBillingPlan,
    created,
    // Cloud-prem leaves a missing bootstrap undefined so the client can report it.
    flags || !cloudPrem ? { ...flags, ...flagOverrides } : undefined,
    organizationName,
  );
  // Cloud-prem has no LaunchDarkly client to carry overrides, so apply them last, with or without a
  // bootstrap. Elsewhere overrides reach flags only through the bootstrap.
  return cloudPrem ? { ...response, ...flagOverrides } : response;
}

export const getFeatureFlagSaga = createSaga(
  getFeatureFlagsInternal,
  "getFeatureFlags",
  {
    sliceName: "featureFlags",
  },
);

slice.saga(getFeatureFlagSaga, {
  start(state, { payload }) {
    state.loading[ROOT] = true;
    state.meta[ROOT] = { organizationId: payload.organizationId };
    delete state.errors[ROOT];
  },
  success(state, { payload }) {
    state.entities[ROOT] = payload;
    delete state.loading[ROOT];
  },
  error(state, { payload }) {
    state.errors[ROOT] = { error: payload };
    delete state.loading[ROOT];
  },
});
